import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import RadioGroup, {
  RadioGroupOption,
} from "../../../../componentLibrary/RadioGroup";
import Input from "../../../../componentLibrary/Input";
import Checkboxes from "../../../../componentLibrary/Checkboxes";

interface Content {
  label: string;
  choices: string[];
  other: boolean;
  msq: boolean;
}

interface MultipleChoiceComponentProps {
  content: Content;
  onChange: (val: string | string[]) => void;
  value?: string | string[];
}

export default function MultipleChoiceComponent({
  content,
  onChange,
  value,
}: MultipleChoiceComponentProps) {
  // Helper: Detects "Other" value (any value not in predefined choices)
  const detectOtherValue = useCallback(
    (values: string[]): [boolean, string] => {
      const cleanValues = values.filter((val) => val !== "Other");
      const otherValue = cleanValues.find(
        (val) => !content.choices.includes(val),
      );
      return [!!otherValue, otherValue || ""];
    },
    [content.choices],
  );

  // Helper: Gets regular options (predefined choices only)
  const getRegularOptions = useCallback(
    (values: string[]): string[] => {
      return values.filter(
        (val) => val !== "Other" && content.choices.includes(val),
      );
    },
    [content.choices],
  );

  // State: [isOtherSelected, otherTextValue]
  const [isOther, setIsOther] = useState<[boolean, string]>(() => {
    if (Array.isArray(value)) {
      return detectOtherValue(value);
    } else if (typeof value === "string") {
      if (value === "Other" || (!content.choices.includes(value) && value)) {
        return [true, value === "Other" ? "" : value];
      }
    }
    return [false, ""];
  });

  // State: Selected predefined choices (for multi-select)
  const [selectedOptions, setSelectedOptions] = useState<string[]>(() => {
    return Array.isArray(value) ? getRegularOptions(value) : [];
  });

  // Ref to prevent useEffect from resetting state when user is selecting "Other"
  const isSelectingOtherRef = useRef(false);

  // Sync state with value prop (but respect user's "Other" selection)
  useEffect(() => {
    if (isSelectingOtherRef.current) return;

    if (Array.isArray(value)) {
      const [hasOther, otherValue] = detectOtherValue(value);
      const regularOptions = getRegularOptions(value);

      // Preserve "Other" selection if user clicked it but hasn't entered text
      if (content.msq && isOther[0] && !hasOther) {
        setSelectedOptions(regularOptions);
        return; // Keep isOther state
      }

      setSelectedOptions(regularOptions);
      setIsOther([hasOther, otherValue]);
    } else if (typeof value === "string" && value) {
      if (value === "Other" || !content.choices.includes(value)) {
        setIsOther([true, value === "Other" ? "" : value]);
        setSelectedOptions([]);
      } else {
        setIsOther([false, ""]);
        setSelectedOptions([]);
      }
    } else {
      // Only reset if "Other" isn't selected
      if (!isOther[0]) {
        setIsOther([false, ""]);
        setSelectedOptions([]);
      }
    }
  }, [
    value,
    content.choices,
    content.msq,
    detectOtherValue,
    getRegularOptions,
  ]);

  // Single choice handlers
  const handleSingleChoiceChange = (option: RadioGroupOption | null) => {
    if (!option) {
      onChange("");
      isSelectingOtherRef.current = false;
      setIsOther([false, ""]);
      return;
    }

    const optionValue = String(option.value);
    if (optionValue === "Other") {
      isSelectingOtherRef.current = true;
      setIsOther([true, isOther[1]]);
      onChange(isOther[1] || "Other");
      // Flag will be cleared when user starts typing in the input field
    } else {
      isSelectingOtherRef.current = false;
      setIsOther([false, ""]);
      onChange(optionValue);
    }
  };

  const handleSingleOtherInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = e.target.value;
    setIsOther([true, newValue]);
    onChange(newValue || "Other");
    // Clear the flag once user starts interacting with the input
    if (newValue) {
      isSelectingOtherRef.current = false;
    }
  };

  // Multi-select handlers
  const handleMultipleChange = (newSelectedOptions: string[]) => {
    const isOtherSelected = newSelectedOptions.includes("Other");
    const filteredOptions = getRegularOptions(newSelectedOptions);

    setSelectedOptions(filteredOptions);

    if (isOtherSelected) {
      const otherText = isOther[1];
      isSelectingOtherRef.current = true;
      setIsOther([true, otherText]);

      if (otherText?.trim()) {
        onChange([...filteredOptions, otherText]);
        // Flag will be cleared when user starts typing in the input field
      } else {
        // Include "Other" marker to persist the selection even without text
        onChange([...filteredOptions, "Other"]);
        // Keep flag set so "Other" stays checked
      }
    } else {
      isSelectingOtherRef.current = false;
      setIsOther([false, ""]);
      onChange(filteredOptions);
    }
  };

  const handleMultipleOtherInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = e.target.value;
    setIsOther([true, newValue]);

    if (newValue) {
      isSelectingOtherRef.current = false;
    }

    const currentRegularOptions = Array.isArray(value)
      ? getRegularOptions(value)
      : selectedOptions;

    onChange(
      newValue ? [...currentRegularOptions, newValue] : currentRegularOptions,
    );
  };

  // Display helpers
  const getSelectedValue = useCallback((): string => {
    if (typeof value === "string" && value) {
      if (value === "Other" || !content.choices.includes(value)) {
        return "Other";
      }
      return value;
    }
    return isOther[0] ? "Other" : "";
  }, [value, content.choices, isOther]);

  const getMultipleSelectedOptions = useCallback((): string[] => {
    if (Array.isArray(value)) {
      const regularOptions = getRegularOptions(value);
      const hasOtherValue = value.some(
        (val) => val !== "Other" && !content.choices.includes(val),
      );
      return hasOtherValue || isOther[0]
        ? [...regularOptions, "Other"]
        : regularOptions;
    }
    return isOther[0] ? [...selectedOptions, "Other"] : selectedOptions;
  }, [value, content.choices, isOther, selectedOptions, getRegularOptions]);

  // Memoized options
  const regularOptions = useMemo(
    () =>
      content.choices.map((choice) => ({
        label: choice,
        value: choice,
      })),
    [content.choices],
  );

  const allOptions = useMemo(() => {
    const options = [...regularOptions];
    if (content.other && !content.msq) {
      options.push({ label: "Other", value: "Other" });
    }
    return options;
  }, [regularOptions, content.other, content.msq]);

  const multipleChoiceOptions = useMemo(() => {
    const options = [...content.choices];
    if (content.other) {
      options.push("Other");
    }
    return options;
  }, [content.choices, content.other]);

  return (
    <div>
      <div className="mt-4 px-4">
        <div
          className="mb-2 font-bold"
          style={{
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          {content.label}
        </div>
        {content.msq ? (
          <div>
            <Checkboxes
              options={multipleChoiceOptions}
              selectedOptions={getMultipleSelectedOptions()}
              setSelectedOptions={handleMultipleChange}
            />
            {content.other && isOther[0] && (
              <Input
                placeholder="Specify other"
                className="mt-2"
                value={isOther[1]}
                onChange={handleMultipleOtherInputChange}
                aria-label="Specify other option"
              />
            )}
          </div>
        ) : (
          <div>
            <RadioGroup
              options={allOptions}
              selectedValue={getSelectedValue()}
              onChange={handleSingleChoiceChange}
            />
            {content.other && isOther[0] && (
              <Input
                placeholder="Specify other"
                className="mt-2"
                value={isOther[1]}
                onChange={handleSingleOtherInputChange}
                aria-label="Specify other option"
                autoFocus
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
